// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Optional device signals used by course-mapping capture and battery telemetry.
//
// expo-battery and expo-device ARE dependencies now (added with battery telemetry). They are
// still loaded through an optional require so a JS-only bundle running against an older native
// build — Expo Go, or an OTA update that outran the store release — degrades to "unknown"
// instead of throwing at import time.
//
// Note what installing expo-battery turned on: the 20% battery floor in courseTrace.ts and
// shotTracking.ts had never fired, because getBatteryFraction always resolved to null. It is
// live from this release, so course-mapping and shot tracking now refuse to start below 20%.

type BatteryModule = {
  getBatteryLevelAsync?: () => Promise<number>;
  getPowerStateAsync?: () => Promise<{
    batteryLevel?: number;
    batteryState?: number;
    lowPowerMode?: boolean;
  }>;
};
type DeviceModule = { modelName?: string | null };

/** expo-battery's BatteryState.CHARGING / FULL. Anything else counts as discharging. */
const BATTERY_STATE_CHARGING = 2;
const BATTERY_STATE_FULL = 3;

let batteryModule: BatteryModule | null = null;
try {
  batteryModule = require('expo-battery') as BatteryModule;
} catch {
  batteryModule = null;
}

let deviceModule: DeviceModule | null = null;
try {
  deviceModule = require('expo-device') as DeviceModule;
} catch {
  deviceModule = null;
}

/**
 * Current battery level as a 0..1 fraction, or null when it cannot be determined.
 *
 * Null means "unknown", not "empty" — callers must not treat it as a reason to stop.
 */
export const getBatteryFraction = async (): Promise<number | null> => {
  if (!batteryModule?.getBatteryLevelAsync) return null;
  try {
    const level = await batteryModule.getBatteryLevelAsync();
    return typeof level === 'number' && level >= 0 && level <= 1 ? level : null;
  } catch {
    return null;
  }
};

export type DevicePowerState = {
  /** Whole percent, or null when unknown. Never 0 as a stand-in for "unreadable". */
  levelPct: number | null;
  charging: boolean;
  lowPower: boolean;
};

/**
 * Level, charging state and low-power mode in one call — the natural shape of a per-minute
 * battery sample, and one binder round-trip rather than three.
 *
 * expo-battery answers -1 for the level where the platform cannot report it (notably web), which
 * becomes null here rather than travelling as a number.
 */
export const getPowerState = async (): Promise<DevicePowerState> => {
  if (!batteryModule?.getPowerStateAsync) {
    return { levelPct: null, charging: false, lowPower: false };
  }
  try {
    const state = await batteryModule.getPowerStateAsync();
    const level = state.batteryLevel;
    return {
      levelPct:
        typeof level === 'number' && level >= 0 && level <= 1 ? Math.round(level * 100) : null,
      charging: state.batteryState === BATTERY_STATE_CHARGING || state.batteryState === BATTERY_STATE_FULL,
      lowPower: state.lowPowerMode === true,
    };
  } catch {
    return { levelPct: null, charging: false, lowPower: false };
  }
};

/** Whether battery readings are available at all on this build. */
export const hasBatteryModule = (): boolean => typeof batteryModule?.getPowerStateAsync === 'function';

export const getDeviceModel = (): string | null => deviceModule?.modelName ?? null;
